import { useMemo } from "react";
import { collection, orderBy } from "firebase/firestore";
import { Table, Text } from "@mantine/core";
import { useFirestore } from "../../helpers/firebaseContext";
import { useLivePagedQuery } from "./useLivePagedQuery";
import { CtcPager, CtcQueryError } from "./ctc_pager";

interface HistoryRow {
    id?: string;
    msg?: string;
    by?: string;
    user?: { email?: string };
    date?: { toDate?: () => Date };
    details?: Record<string, unknown>;
}

const summarize = (details?: Record<string, unknown>) => {
    if (!details) return "";
    const parts = Object.entries(details)
        .filter(([, v]) => v !== null && v !== undefined && v !== "")
        .map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : String(v)}`);
    return parts.join(" · ");
};

/**
 * Live, paginated audit trail of a purchase order (ctc_purchase_orders/{id}/history).
 * The portal's generic History component loads once with getDocs; this one streams, so
 * automation results, WhatsApp answers and other users' actions appear as they happen.
 */
export const CtcHistory = ({ poId }: { poId: string }) => {
    const db = useFirestore();
    const ref = useMemo(() => collection(db, "ctc_purchase_orders", poId, "history"), [db, poId]);
    const constraints = useMemo(() => [orderBy("date", "desc")], []);
    const page = useLivePagedQuery<HistoryRow>(ref, constraints, `history:${poId}`, 15);

    return (
        <>
            <CtcQueryError error={page.error} />
            <Table striped withTableBorder>
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th w={170}>When</Table.Th>
                        <Table.Th>What</Table.Th>
                        <Table.Th w={220}>By</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {page.rows.map((row) => (
                        <Table.Tr key={row.id}>
                            <Table.Td><Text size="xs">{row.date?.toDate?.().toLocaleString("en-GB", { hour12: true }) ?? "…"}</Text></Table.Td>
                            <Table.Td>
                                <Text size="sm">{row.msg}</Text>
                                {row.details && <Text size="xs" c="dimmed" lineClamp={2}>{summarize(row.details)}</Text>}
                            </Table.Td>
                            <Table.Td><Text size="xs">{row.by ?? row.user?.email}</Text></Table.Td>
                        </Table.Tr>
                    ))}
                    {!page.rows.length && !page.loading && <Table.Tr><Table.Td colSpan={3}><Text size="sm" c="dimmed">No history yet.</Text></Table.Td></Table.Tr>}
                </Table.Tbody>
            </Table>
            <CtcPager page={page} />
        </>
    );
};

export default CtcHistory;
